"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PROVISION_REFUSALS = exports.provisionTenantResponseSchema = exports.provisionTenantRequestSchema = void 0;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Provisioning — the first tenant, the first workspace, the first member.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `chunks.md` puts *"tenant administration"* in **Later**, and this is its first half. Before it,
 * the only two things in the system that could create a membership were `scripts/seed-dev.mjs` —
 * six statements against a hardcoded tenant — and `accept_workspace_invitation`, which needs an
 * invitation, which needs a workspace, which needs somebody already in one. So a confirmed account
 * that nobody had invited anywhere landed on *"A fiókod még egyetlen munkaterülethez sem
 * tartozik"* and could go nowhere. Sign-up is self-serve; this is what makes that true.
 *
 * ⚠️ **ONE OPERATION, NOT TWO.** A tenant with no workspace is unreachable — every screen is
 * workspace-scoped — and a workspace with no OWNER is a workspace nobody can administer. Creating
 * them separately would mean two requests with a state between them that no screen can render, so
 * the tenant, its first workspace, the `public.users` mirror, the member, the built-in roles, the
 * task vocabularies, the metafields and the OWNER grant are one transaction. `0031` is where.
 *
 * ⚠️ **This is ONBOARDING, and it is deliberately not "create a workspace".** It refuses an account
 * that is already a member of anything (`already_member`). Two reasons, and the second is the load
 * bearing one:
 *
 *  * a second workspace inside an existing tenant is a different operation with a different
 *    authority — it is the tenant's, not a member's, and there is no tenant-level permission in the
 *    catalogue to hang it on. Inventing one would be rule 8;
 *  * without the refusal, one confirmed address can mint unbounded tenants. There is no superadmin
 *    to notice and no rate limit that would distinguish it from real signups.
 *
 * ⚠️ **The workspace SWITCHER (R-3) is still deferred, which is why the refusal costs nothing
 * today**: a second workspace would have no entrance even if this created one.
 */
/**
 * ⚠️ **The wire says `tenant`; the screen says company.** The model's word is `tenant` and this
 * contract names the model — but nobody signing up knows what a tenant is, so `welcome-screen.tsx`
 * labels the field *"Cég neve"* / *"Company name"*. Renaming the field to match the label would put
 * a UI word into the schema that `0003`'s table does not use.
 *
 * ⚠️ **No SLUGS on the request, and that is a decision rather than an omission.** Both are derived
 * server-side by `slugFrom`, because neither is visible anywhere: `0022` records that ours is
 * unique *within a tenant*, that nothing serves a subdomain and that no URL in this product
 * contains it — which is also why there is no reserved-word list here, where the mockup's global
 * subdomain has one. A collision on the tenant slug is resolved by suffixing rather than refused,
 * since refusing would mean telling somebody their *company name* is taken because of a string they
 * never saw. The workspace slug cannot collide: its tenant is one statement old.
 *
 * ⚠️ **When a subdomain ever exists the tenant slug becomes user-visible**, and at that point this
 * needs to become a chosen-and-checked field with the availability endpoint `/workspace`
 * already has. Recorded here rather than pre-built.
 */
exports.provisionTenantRequestSchema = zod_1.z.object({
    /* The bounds are `0003`'s own check: `length(btrim(name)) between 1 and 200`. */
    tenantName: zod_1.z.string().trim().min(1).max(200),
    workspaceName: zod_1.z.string().trim().min(1).max(200),
});
/**
 * ⚠️ **The slug comes back from the write, and `0027` explains why it has to.** Reading
 * `public.workspaces` after provisioning on an unscoped connection returns nothing — the table is
 * RLS'd to your own memberships and the caller is holding no scope, having acquired the membership
 * a moment ago inside a `security definer` function. The invitation flow shipped that bug first and
 * answered `workspaceSlug: null` on a successful join.
 */
exports.provisionTenantResponseSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    workspaceId: zod_1.z.string(),
    memberId: zod_1.z.string(),
    workspaceSlug: zod_1.z.string(),
});
/**
 * Why provisioning refused.
 *
 * ⚠️ **`unsluggable_name` is an ADDED error code** (rule 8), and it exists because the alternative
 * is worse. `slugFrom` returns `''` for a name with fewer than three sluggable characters — `"Kft"`
 * is fine, `"AB"` and `"?!"` are not — and without its own code that refusal arrives as a generic
 * `validation_failed` against a field the person filled in correctly. The screen needs to be able
 * to say *which* rule was broken, because "company name" and "at least three letters or digits"
 * are not obviously the same subject.
 */
exports.PROVISION_REFUSALS = ['already_member', 'unsluggable_name'];
//# sourceMappingURL=contract.js.map